import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'
import type {AhpProvisioningTarget, PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {makeFileReference, makeSnippetReference} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {PersistedTaskComposerScope} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import type {CopilotChatRepo, LineRange} from '@github-ui/copilot-chat-types'
import type {AhpSpaceSelection} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {Repository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {buildPromptWithReferenceContext as buildSharedPromptWithReferenceContext} from '@github-ui/copilot-shared-ui/composer/build-prompt-with-reference-context'

import {DEFAULT_MCP_PERMISSION_MODE, type McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import type {ReactNode} from 'react'
import type {EnvironmentSubmit} from './use-environment-selection'
import type {HandleUserSubmit} from './user-submit'

export const MCP_PERMISSION_GATE_FEATURE_FLAG = 'copilot_swe_agent_mcp_permission_gate'
export const DEFAULT_TASK_COMPOSER_SCOPE: TaskComposerScope = {kind: 'personal'}
export const ACTIONS_AGENT_MODES: readonly AgentMode[] = ['interactive', 'autopilot']
export const SESSION_AGENT_MODES: readonly AgentMode[] = ['interactive', 'plan', 'autopilot']

export function availableAgentModes(isSessionModeDraft: boolean): readonly AgentMode[] {
  return isSessionModeDraft && isFeatureEnabled('one_copilot_plan_mode') ? SESSION_AGENT_MODES : ACTIONS_AGENT_MODES
}

export function resolveAvailableAgentMode(
  selectedMode: AgentMode | undefined,
  isSessionModeDraft: boolean,
): AgentMode | undefined {
  if (!selectedMode) return undefined
  const availableModes = availableAgentModes(isSessionModeDraft)
  return availableModes.includes(selectedMode) ? selectedMode : availableModes[0]
}

export interface AvailableOrganization {
  id: string
  login: string
  name: string
  avatarUrl: string
  requiresSso: boolean
}

export type TaskComposerScope = {kind: 'personal'} | {kind: 'organization'; organization: AvailableOrganization}

/**
 * Reduce a full `TaskComposerScope` to the small shape we persist across
 * sessions (org login only — orgs come from the payload on next mount).
 */
export function toPersistedScope(scope: TaskComposerScope): PersistedTaskComposerScope {
  if (scope.kind === 'organization') return {kind: 'organization', login: scope.organization.login}
  return {kind: 'personal'}
}

/**
 * Resolves a persisted scope against the current list of available
 * organizations. Falls back to `DEFAULT_TASK_COMPOSER_SCOPE` when the persisted
 * org has been removed, revoked, or now requires SSO — the composer treats
 * SSO-required orgs as unselected on load so an unresolved SSO handshake
 * cannot silently pin scope to an org the user cannot use.
 */
export function resolvePersistedScope(
  persisted: PersistedTaskComposerScope | null | undefined,
  availableOrganizations: readonly AvailableOrganization[],
): TaskComposerScope {
  if (!persisted) return DEFAULT_TASK_COMPOSER_SCOPE
  if (persisted.kind === 'personal') return {kind: 'personal'}
  const match = availableOrganizations.find(org => org.login === persisted.login)
  if (!match) return DEFAULT_TASK_COMPOSER_SCOPE
  if (match.requiresSso) return DEFAULT_TASK_COMPOSER_SCOPE
  return {kind: 'organization', organization: match}
}

/** Owner shared by Sandbox provisioning and repository context, for personal and organization scopes. */
export function scopeLogin(scope: TaskComposerScope, currentUserLogin: string | undefined): string | undefined {
  return scope.kind === 'organization' ? scope.organization.login : currentUserLogin
}

/**
 * The scope a draft may actually be filtered by.
 *
 * Owner scope only exists on Sandbox, so anywhere else the picker is
 * withheld and a scope left over from an earlier Sandbox draft must not apply.
 * The attach pickers and the attachment controller both resolve their owner
 * through this, so the repositories a picker offers and the ones an attachment
 * keeps cannot disagree — otherwise a repo the picker still lists is accepted
 * and then silently dropped before submission.
 */
export function activeComposerScope(
  scope: TaskComposerScope,
  isSandboxEnvironment: boolean,
): TaskComposerScope | undefined {
  return isSandboxEnvironment ? scope : undefined
}

/**
 * Returns true when a repo remains valid under the active scope.
 *
 * A repo is in scope when:
 *   - no scope owner is active, or
 *   - the repo owner matches the scope owner (case-insensitive).
 *
 * Both personal and org scopes filter strictly by owner, so repos owned by
 * anyone else (public or private) are dropped when a scope owner is set.
 */
export function isRepoInScope(
  repo: {owner?: string; private?: boolean} | null | undefined,
  scopeOwner: string | null | undefined,
): boolean {
  if (!repo) return true
  if (scopeOwner === undefined) return true
  if (!scopeOwner) return false
  return (repo.owner ?? '').toLowerCase() === scopeOwner.toLowerCase()
}

/**
 * Returns true when an already-attached repo remains valid under the active
 * scope owner.
 *
 * Uses the same owner policy as repository selection, including public repos.
 */
export function isAttachedRepoInScope(
  repo: {name: string; private?: boolean},
  scopeOwner: string | null | undefined,
): boolean {
  return isRepoInScope({owner: repo.name.split('/')[0]}, scopeOwner)
}

/**
 * Scope hint shown under a repo picker's title. Standing in for the in-list
 * section titles requested in github-ui#31409 (the shared FilteredActionList
 * renders a literal "Group <id>" for header-less groups), it names the owner
 * whose repositories are shown so both the environment and attachment pickers
 * surface the active scope consistently.
 */
export function repoPickerScopeSubtitle(
  scopeOwner: string | null | undefined,
  currentUserLogin: string | undefined,
): string {
  if (scopeOwner === null || scopeOwner === '') return 'Resolving repository owner'
  if (!scopeOwner) return 'Your repositories and public repositories'
  if (scopeOwner.toLowerCase() === currentUserLogin?.toLowerCase()) return 'Your repositories'
  return `Repositories in @${scopeOwner}`
}

/**
 * Resolves whether the MCP permission gate UI is enabled.
 * The prop is authoritative when provided (true or false); an omitted prop
 * falls back to the legacy `copilot_swe_agent_mcp_permission_gate` feature flag.
 */
export function resolveMcpPermissionGate(mcpPermissionGateEnabled?: boolean): boolean {
  return mcpPermissionGateEnabled ?? isFeatureEnabled(MCP_PERMISSION_GATE_FEATURE_FLAG)
}

/**
 * Resolves the MCP tool-permission mode submitted with a task.
 *
 * An explicit approval-mode selection is authoritative: deriving consent from the agent mode
 * alone let Autopilot submit `allow_all` while the picker said "Always ask". Only `allow-all`
 * grants blanket consent; `assisted` has no MCP equivalent, so it gates.
 */
export function resolveMcpPermissionMode({
  approvalMode,
  agentMode,
  fallback,
}: {
  approvalMode?: SessionApprovalMode
  agentMode?: AgentMode
  fallback?: McpPermissionMode
}): McpPermissionMode | undefined {
  if (approvalMode) return approvalMode === 'allow-all' ? 'allow_all' : DEFAULT_MCP_PERMISSION_MODE
  if (agentMode) return agentMode === 'autopilot' ? 'allow_all' : DEFAULT_MCP_PERMISSION_MODE
  return fallback
}

export function canPrepareSandboxChat({
  chatEnabled,
  hasEnvironmentSubmit,
  hideRepoSelector,
  showSandboxOption,
  hasSpecifiedRepository,
  scopeLogin: launchScopeLogin,
}: {
  chatEnabled: boolean
  hasEnvironmentSubmit: boolean
  hideRepoSelector: boolean
  showSandboxOption: boolean
  hasSpecifiedRepository: boolean
  scopeLogin?: string
}): boolean {
  return (
    chatEnabled &&
    hasEnvironmentSubmit &&
    !hideRepoSelector &&
    showSandboxOption &&
    !hasSpecifiedRepository &&
    Boolean(launchScopeLogin)
  )
}

export function canPrepareTaskComposerChat({
  chatLaunch,
  hasEnvironmentSubmit,
  hideRepoSelector,
  showSandboxOption,
  hasSpecifiedRepository,
  scopeLogin: launchScopeLogin,
}: {
  chatLaunch?: ChatLaunchAvailability
  hasEnvironmentSubmit: boolean
  hideRepoSelector: boolean
  showSandboxOption: boolean
  hasSpecifiedRepository: boolean
  scopeLogin?: string
}): boolean {
  // A handoff neither provisions nor binds an environment, so the Sandbox
  // prerequisites below would only ever disable a destination that does not
  // need them. It still respects an explicitly specified repository, which
  // means the caller asked for a task rather than Chat.
  if (chatLaunch?.kind === 'handoff') return !hasSpecifiedRepository

  const hasAvailableLauncher = chatLaunch?.kind === 'environment' && hasEnvironmentSubmit && Boolean(launchScopeLogin)

  return hasAvailableLauncher && !hideRepoSelector && showSandboxOption && !hasSpecifiedRepository
}

export function getDraftStorageKeySuffix(eventType: string | undefined, repoNameWithOwner: string | undefined): string {
  const base = eventType ?? ''
  return repoNameWithOwner ? `${base}:${repoNameWithOwner}` : base
}

export function getChatDraftStorageKeySuffix(eventType: string | undefined): string {
  return getDraftStorageKeySuffix(eventType, 'chat')
}

export function buildPromptWithReferenceContext(
  ...args: Parameters<typeof buildSharedPromptWithReferenceContext>
): ReturnType<typeof buildSharedPromptWithReferenceContext> {
  return buildSharedPromptWithReferenceContext(...args)
}

export type CreateTaskReference = {
  path: string
  range?: LineRange
}

export function makeFileOrSnippetReference({
  referenceRepo,
  reference,
}: {
  referenceRepo: CopilotChatRepo
  reference: CreateTaskReference
}) {
  if (reference.range) {
    return makeSnippetReference(reference.path, reference.range, referenceRepo)
  } else {
    return makeFileReference(reference.path, referenceRepo)
  }
}

export type TaskComposerProps = {
  handleUserSubmit: HandleUserSubmit
  /** Exact CLI session mode selected by hosts that expose the three-mode picker. */
  agentMode?: AgentMode
  approvalMode?: SessionApprovalMode
  /** Replaces the legacy persisted MCP permission selector for hosts migrating to independent mode controls. */
  modePicker?: (state: {availableModes: readonly AgentMode[]; supportsApprovalMode: boolean}) => ReactNode
  initialRepo?: Repository | null
  /** Serializable repository identifier resolved by the task-composer repo loader. */
  initialRepositoryNameWithOwner?: string
  initialReference?: CreateTaskReference | null
  hideRepoSelector?: boolean
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  className?: string
  eventType?: string
  shouldClearTextOnSubmit?: boolean
  /** Locks the composer while a cloud task submits. AHP submits always lock. */
  disableWhileSubmitting?: boolean
  /** Prevents Sandbox/AHP submission while the host finishes initialization. */
  disableSandboxSubmission?: boolean
  initialText?: string
  /** Migrates a failed Quick Chat prompt into its scoped draft before clearing the one-shot recovery entry. */
  enableQuickChatDraftRecovery?: boolean
  initialPreparedPrompt?: PreparedPrompt
  /** Mount-scoped Chat attachments displayed in the composer before its first manual submit. */
  initialChatAttachments?: readonly MessageAttachment[]
  /** Space selected by a mount-scoped Chat prompt. */
  initialSpace?: AhpSpaceSelection
  /** Destination hint retained across a handoff until the mounted composer resolves its actual launcher capability. */
  initialChatProvisioningTarget?: AhpProvisioningTarget
  initialBranch?: string
  isNewConversation?: boolean
  maxTextareaHeight?: number
  /** When true, requires Cmd/Ctrl+Enter to submit instead of plain Enter. Defaults to false. */
  submitWithModifier?: boolean
  /**
   * Called when the user submits with an AHP environment selected; when omitted,
   * submit falls back to the default cloud path. `repositoryNwo`/`baseRef` carry
   * the selected repo so the task can be provisioned as a worktree.
   */
  onEnvironmentSubmit?: EnvironmentSubmit
  /** Selects exactly one repository-less Chat creation path. */
  chatLaunch?: ChatLaunch
  /** Identifies one application of the preset that selects Sandbox Chat mode. */
  newChatPresetId?: string
  /**
   * Offers the managed-sandbox picker option (requires `onEnvironmentSubmit`).
   * Also gated by the managed-sandbox and AHP environments flags.
   */
  supportsManagedSandbox?: boolean
  agentQuotaSnapshot?: AgentQuotaSnapshot
  /**
   * Authoritative override for the MCP permission-mode selector when provided:
   * `true` forces it visible, `false` forces it hidden. When omitted, visibility
   * falls back to the `copilot_swe_agent_mcp_permission_gate` feature flag.
   */
  mcpPermissionGateEnabled?: boolean
  /** Login of the current user, used to pre-fill the owner on the new repository URL. */
  currentUserLogin?: string
  /** Avatar URL of the current user, used as the personal-scope avatar in the scope picker. */
  currentUserAvatarUrl?: string
  /** Organizations the user can scope the composer to. Empty/omitted hides the scope picker. */
  availableOrganizations?: AvailableOrganization[]
  /** Org login seeding the scope default when the user has no prior selection. */
  lastContributedOrganizationLogin?: string
  /** Explicit facade input; when present it takes precedence over URL target defaults. */
  initialTarget?: 'chat' | 'task'
  /** Allows a task draft to carry implicit page context when its host surface opts in. */
  includeImplicitContextForTask?: boolean
}

export type ChatSubmissionMetadata = Readonly<Record<string, unknown>> & {
  space?: AhpSpaceSelection
  repositoryNameWithOwner?: string
  agentMode?: AgentMode
  mcpPermissionMode?: McpPermissionMode
  /** Account the draft is scoped to, so the handoff can honor the picked owner. */
  scopeLogin?: string
}

/**
 * How an accepted Chat turn becomes a session.
 *
 * `environment` provisions here, so it depends on the Sandbox environment being
 * offered and selected. `handoff` gives the prepared turn to the host for
 * navigation or in-place runtime provisioning, so environment availability must
 * not decide whether the turn can be composed.
 */
export type ChatLaunchAvailability = {kind: 'environment'} | {kind: 'handoff'}

export type ChatLaunch =
  | {
      kind: 'handoff'
      availability?: 'selected' | 'implicit'
      /** Keeps an explicitly selected Sandbox repository on the Chat handoff path. */
      submitRepositoryAsChat?: boolean
      onSubmit: (prompt: PreparedPrompt, metadata?: ChatSubmissionMetadata) => void | Promise<void>
    }
  | {
      kind: 'environment'
      availability?: 'selected' | 'implicit'
    }
